import {
  all, takeLatest, select, put,
} from 'redux-saga/effects';

import { RootState } from 'reducers/rootReducer';
import { PayloadAction } from 'types/payloadAction';
import { CoordinatePoint } from 'types/point';
import { parseDrawInput } from 'helpers/draw/parseDrawInput';
import {
  START_DRAW, FINISH_SEGMENT,
  selectors as drawSelectors,
  actions as drawActions,
  DrawState,
} from 'ducks/draw/draw';
import { isTooShort } from 'helpers/model/wallPoints';
import { selectors as settingsSelectors } from 'ducks/settings';
import { divideBy } from 'helpers/utils';
import { Direction } from 'types/direction';
import { selectors as viewportSelectors } from 'ducks/viewport';
import { selectors as editModeSelectors } from 'ducks/editMode';

const NAME = 'drawWithKeypad';

const ADD_DRAW_INPUT = `${NAME}/ADD_DRAW_INPUT`;
const FINISH_DRAW_INPUT = `${NAME}/FINISH_DRAW_INPUT`;
const RESET_DRAW_INPUT = `${NAME}/RESET_DRAW_INPUT`;
const RESET_CURRENT_VALUE = `${NAME}/RESET_CURRENT_VALUE`;
const RESET_CURRENT_ANGLE_VALUE = `${NAME}/RESET_CURRENT_ANGLE_VALUE`;
const RESET_CURRENT_DIRECTION = `${NAME}/RESET_CURRENT_DIRECTION`;
const REMOVE_LAST_INPUT = `${NAME}/REMOVE_LAST_INPUT`;
const SET_INPUT_SEQUENCE = `${NAME}/SET_INPUT_SEQUENCE`;
const SET_ANGLE_INPUT = `${NAME}/SET_ANGLE_INPUT`;
const SET_CURRENT_VALUE = `${NAME}/SET_CURRENT_VALUE`;
const SET_CURRENT_ANGLE_VALUE = `${NAME}/SET_CURRENT_ANGLE_VALUE`;
const SET_CURRENT_DIRECTION = `${NAME}/SET_CURRENT_DIRECTION`;
const SET_NUMPAD_LOCK = `${NAME}/SET_NUMPAD_LOCK`;
const SET_UNHIDE_MOUSE = `${NAME}/SET_UNHIDE_LOCK`;

// Utility input detectors
const isDigitKey = (key: string): boolean => /^[0-9]$/.test(key);
const isDecimalKey = (key: string): boolean => key === '.' || key === ',';
const isDirectionKey = (key: string): key is Direction =>
  Object.values(Direction).includes(key as Direction);


// Action creators
export const actions = {
  addDrawInput: (inputKey: string) => ({
    type: ADD_DRAW_INPUT,
    payload: inputKey,
  }),
  finishDrawInput: () => ({
    type: FINISH_DRAW_INPUT,
  }),
  resetDrawInput: () => ({
    type: RESET_DRAW_INPUT,
  }),
  resetCurrentValue: () => ({
    type: RESET_CURRENT_VALUE,
  }),
  resetCurrentAngleValue: () => ({
    type: RESET_CURRENT_ANGLE_VALUE,
  }),
  resetCurrentDirection: () => ({
    type: RESET_CURRENT_DIRECTION,
  }),
  removeLastInput: () => ({
    type: REMOVE_LAST_INPUT,
  }),
  setInputSequence: (inputKeySeqeunce: string[]) => ({
    type: SET_INPUT_SEQUENCE,
    payload: inputKeySeqeunce
  }),
  setAngleInput: () => ({
    type: SET_ANGLE_INPUT,
  }),
  setCurrentValue: (inputKey: string) => ({
    type: SET_CURRENT_VALUE,
    payload: inputKey,
  }),
  setCurrentAngleValue: (inputKey: string) => ({
    type: SET_CURRENT_ANGLE_VALUE,
    payload: inputKey,
  }),
  setCurrentDirection: (inputKey?: string) => ({
    type: SET_CURRENT_DIRECTION,
    payload: inputKey,
  }),
  setNumpadLock: (inputKey: boolean) => ({
    type: SET_NUMPAD_LOCK,
    payload: inputKey,
  }),
  revealMouse: () => ({
    type: SET_UNHIDE_MOUSE,
  }),
};

export interface DrawKeypadState {
  readonly inputKeySequence: string[];
  readonly currentValue: string;
  readonly currentAngleValue: string;
  readonly currentDirection: Direction | undefined;
  readonly isAngleInputEnabled: boolean;
  readonly isNumpadLocked: boolean;
  readonly hideMouse: boolean;
}

// Initial state
const initialState: DrawKeypadState = {
  inputKeySequence: [],
  currentValue: '',
  currentAngleValue: '0',
  currentDirection: undefined,
  isAngleInputEnabled: false,
  isNumpadLocked: false,
  hideMouse: false
};

const addDrawInputReducer = (state: DrawKeypadState, inputKey: string): DrawKeypadState => {
  const last = state.inputKeySequence[state.inputKeySequence.length - 1];
  const lastWasDirection = last && isDirectionKey(last);
  const thisIsDirection = isDirectionKey(inputKey);

  if (thisIsDirection && lastWasDirection) {
    const newSequence = [...state.inputKeySequence];
    newSequence[newSequence.length - 1] = inputKey;

    return {
      ...state,
      inputKeySequence: newSequence,
      isNumpadLocked: true,
      hideMouse: true,
    };
  }

  return {
    ...state,
    inputKeySequence: [
      ...state.inputKeySequence,
      inputKey,
    ],
    isNumpadLocked: true,
    hideMouse: true,
  };
};

const resetDrawInputReducer = (state: DrawKeypadState): DrawKeypadState => ({
  ...state,
  inputKeySequence: [],
  currentValue: '',
  currentDirection: undefined,
  currentAngleValue: '0',
  isAngleInputEnabled: false
});

const resetCurrentValueReducer = (state: DrawKeypadState): DrawKeypadState => ({
  ...state,
  currentValue: '',
});

const resetCurrentAngleValueReducer = (state: DrawKeypadState): DrawKeypadState => ({
  ...state,
  currentAngleValue: '',
});

const resetCurrentDirectionReducer = (state: DrawKeypadState): DrawKeypadState => ({
  ...state,
  currentDirection: undefined,
});

const setInputSequenceReducer = (state: DrawKeypadState, inputKeySequence: string[]): DrawKeypadState => ({
  ...state,
  inputKeySequence
});

const setAngleInputReducer = (state: DrawKeypadState): DrawKeypadState => ({
  ...state,
  isAngleInputEnabled: !state.isAngleInputEnabled,
});

const setCurrentValueReducer = (state: DrawKeypadState, inputKey: string): DrawKeypadState => ({
  ...state,
  currentValue: inputKey
});
const setCurrentAngleValueReducer = (state: DrawKeypadState, inputKey: string): DrawKeypadState => ({
  ...state,
  currentAngleValue: inputKey
});
// setCurrentDirection is dispatched with no key once a direction has been committed. The blank
// entry is a sequence break: MainScreen tests the last entry to decide whether the next arrow
// key needs a fresh magnitude, so it must still be appended.
const SEQUENCE_BREAK = '';
const setCurrentDirectionReducer = (state: DrawKeypadState, inputKey?: string): DrawKeypadState => {
  const last = state.inputKeySequence[state.inputKeySequence.length - 1];
  const lastWasDirection = last && isDirectionKey(last);
  const thisIsDirection = inputKey !== undefined && isDirectionKey(inputKey);
  const direction = thisIsDirection ? inputKey : undefined;

  if (thisIsDirection && lastWasDirection) {
    const newSequence = [...state.inputKeySequence];
    newSequence[newSequence.length - 1] = inputKey;
    return { ...state, inputKeySequence: newSequence, currentDirection: direction };
  }

  return {
    ...state,
    inputKeySequence: [...state.inputKeySequence, inputKey ?? SEQUENCE_BREAK],
    currentDirection: direction
  };
};
const setNumpadLockReducer = (state: DrawKeypadState, isNumpadLocked: boolean): DrawKeypadState => {
  // if locking, just set the lock to true
  if (isNumpadLocked) {
    return {
      ...state,
      isNumpadLocked: true,
      hideMouse: true,
    };
  }

  // if removing the lock, reset the sequence
  return {
    ...state,
    isNumpadLocked: false,
    hideMouse: false,
    inputKeySequence: [],
    currentValue: '',
  };
};
const revealMouseReducer = (state: DrawKeypadState): DrawKeypadState => ({
  ...state,
  hideMouse: false,
});

export const reducer = (state: DrawKeypadState = initialState, action: PayloadAction): DrawKeypadState => {
  switch (action.type) {
    case ADD_DRAW_INPUT:
      return addDrawInputReducer(state, action.payload);

    case RESET_DRAW_INPUT:
      return resetDrawInputReducer(state);

    case RESET_CURRENT_VALUE:
      return resetCurrentValueReducer(state);

    case RESET_CURRENT_ANGLE_VALUE:
      return resetCurrentAngleValueReducer(state);

    case RESET_CURRENT_DIRECTION:
      return resetCurrentDirectionReducer(state);

    case SET_INPUT_SEQUENCE:
      return setInputSequenceReducer(state, action.payload);

    case SET_ANGLE_INPUT:
      return setAngleInputReducer(state);

    case SET_CURRENT_VALUE:
      return setCurrentValueReducer(state, action.payload);

    case SET_CURRENT_ANGLE_VALUE:
      return setCurrentAngleValueReducer(state, action.payload);

    case SET_CURRENT_DIRECTION:
      return setCurrentDirectionReducer(state, action.payload);

    case SET_NUMPAD_LOCK:
      return setNumpadLockReducer(state, action.payload);

    case SET_UNHIDE_MOUSE:
      return revealMouseReducer(state);

    case 'editMode/switchToSelection':
      return setNumpadLockReducer(state, false);

    default:
      return state;
  }
};

// Selectors
const getDrawInputState = (rootState: RootState): DrawKeypadState => rootState.drawKeypad;

const getDrawInputSequence = (rootState: RootState): string[] => rootState.drawKeypad.inputKeySequence;

const getCurrentValue = (rootState: RootState): string => rootState.drawKeypad.currentValue;

const getCurrentDirection = (rootState: RootState): string | undefined => rootState.drawKeypad.currentDirection;

const getIsAngleInputEnabled = (rootState: RootState): boolean => rootState.drawKeypad.isAngleInputEnabled;

const getCurrentAngleValue = (rootState: RootState): string => rootState.drawKeypad.currentAngleValue;

const getNumpadLocked = (rootState: RootState): boolean => rootState.drawKeypad.isNumpadLocked;

const isMouseHidden = (rootState: RootState): boolean => rootState.drawKeypad.hideMouse;

export const selectors = {
  getDrawInputState,
  getDrawInputSequence,
  getCurrentValue,
  getCurrentDirection,
  getIsAngleInputEnabled,
  getCurrentAngleValue,
  getNumpadLocked,
  isMouseHidden,
};

// sagas
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const createSagas = () => {
  function* doAddDrawInput(action: any) {
    const { payload } = action;

    const isDigit = isDigitKey(payload);
    const isDecimal = isDecimalKey(payload);
    const isDirection = isDirectionKey(payload);

    const drawState: DrawState = drawSelectors.getDrawState(yield select());
    const drawInputState: DrawKeypadState = selectors.getDrawInputState(yield select());

    if (drawInputState.inputKeySequence &&
      isDirectionKey(drawInputState.inputKeySequence[0])) {
      drawInputState.inputKeySequence.pop();
      return;
    }

    const { isAngleInputEnabled } = drawInputState;
    const uom = settingsSelectors.getUnitOfMeasure(yield select());
    const currentGridSize = settingsSelectors.getGridSizeInFeet(yield select());
    const effectiveSequence = [...drawInputState.inputKeySequence];

    if (drawInputState.currentValue !== '') {
      effectiveSequence.push(drawInputState.currentValue);
    }

    const deltaPoint = parseDrawInput(effectiveSequence, uom);
    const isEditMode = editModeSelectors.isEditMode(yield select());

    if (!isAngleInputEnabled) {
      const endPoint: CoordinatePoint = {
        x: drawState.startPoint.x + divideBy(currentGridSize)(deltaPoint.x),
        y: drawState.startPoint.y + divideBy(currentGridSize)(deltaPoint.y),
      };

      if (isEditMode) {
        yield put(drawActions.moveAnchorPoint(endPoint));
      } else {
        yield put(drawActions.setEndPointWithoutSnapping(endPoint, false));
      }
    }

    // ANGLE MODE — numeric input goes to angle
    if (isAngleInputEnabled && isDigit) {
      const newVal = drawInputState.currentAngleValue.length > 2
        ? drawInputState.currentAngleValue.substring(1) + payload
        : drawInputState.currentAngleValue + payload;

      yield put(actions.setCurrentAngleValue(newVal));
    }

    // NORMAL NUMERIC INPUT
    if ((isDigit || isDecimal) && !isAngleInputEnabled) {
      const newVal = drawInputState.currentValue + payload;
      yield put(actions.setCurrentValue(newVal));
    }

    if (isDirection) {
      if (isDirection) {
        yield put(actions.setCurrentDirection(payload));
        yield put(actions.resetCurrentValue());
      }
    }

    // INVALID SEQUENCE CLEANUP
    if (!isDigit && !isDecimal && drawInputState.inputKeySequence.length > 1 &&
      isDirectionKey(drawInputState.inputKeySequence[drawInputState.inputKeySequence.length - 1])) {
      yield put(actions.setCurrentValue(''));
      yield put(actions.setCurrentDirection(undefined));
    }

    // Prevent multiple decimals
    if (isDecimal && (drawInputState.currentValue.match(/\./g) || []).length > 0) {
      yield put(actions.setCurrentValue(''));
      yield put(actions.setCurrentDirection(undefined));
    }
  }

  function* doFinishDrawInput() {
    const drawState: DrawState = drawSelectors.getDrawState(yield select());
    if (!isTooShort(drawState.startPoint, drawState.endPoint, viewportSelectors.getZoomInPercent(yield select()))) {
      yield put(drawActions.finishSegment());
    }
  }

  function* doResetDrawInput() {
    yield put(actions.resetDrawInput());
  }

  function* doRemoveLastInput() {
    const state: DrawKeypadState = yield select(selectors.getDrawInputState);
    const {
      currentValue,
      inputKeySequence,
      isAngleInputEnabled,
      currentAngleValue,
    } = state;

    if (isAngleInputEnabled) {
      const newAngle = currentAngleValue.slice(0, -1);

      if (newAngle === '') {
        yield put(actions.setAngleInput());
        yield put(drawActions.resetDrawPoints());
        return;
      }

      yield put(actions.setCurrentAngleValue(newAngle));
      yield put(drawActions.resetDrawPoints());
      return;
    }

    //  REMOVE LAST DIGIT ONLY ✅
    if (currentValue !== '') {
      const newValue = currentValue.slice(0, -1);
      yield put(actions.setCurrentValue(newValue));

      if (inputKeySequence.length > 0) {
        const lastKey = inputKeySequence[inputKeySequence.length - 1];
        if (isDigitKey(lastKey) || isDecimalKey(lastKey)) {
          const newSeq = inputKeySequence.slice(0, -1);
          yield put(actions.setInputSequence(newSeq));

          // Recalculate endpoint based on updated sequence
          const uom: string = yield select(settingsSelectors.getUnitOfMeasure);
          const currentGridSize: number = yield select(settingsSelectors.getGridSizeInFeet);
          const drawState: DrawState = yield select(drawSelectors.getDrawState);

          // Build effective sequence with the new value
          const effectiveSequence = [...newSeq];
          if (newValue !== '') {
            effectiveSequence.push(newValue);
          }

          const deltaPoint = parseDrawInput(effectiveSequence, uom);
          const endPoint: CoordinatePoint = {
            x: drawState.startPoint.x + divideBy(currentGridSize)(deltaPoint.x),
            y: drawState.startPoint.y + divideBy(currentGridSize)(deltaPoint.y),
          };

          yield put(drawActions.setEndPointWithoutSnapping(endPoint, false));
        }

        if (newValue === '') {
          yield put(actions.resetCurrentDirection());
        }


        return;
      }
      if (inputKeySequence.length > 0) {
        const newSeq = [...inputKeySequence];
        newSeq.pop();
      }
    }
  }


  return function* saga() {
    yield all([
      takeLatest(START_DRAW, doResetDrawInput),
      takeLatest(FINISH_SEGMENT, doResetDrawInput),
      takeLatest(ADD_DRAW_INPUT, doAddDrawInput),
      takeLatest(FINISH_DRAW_INPUT, doFinishDrawInput),
      takeLatest(REMOVE_LAST_INPUT, doRemoveLastInput),
    ]);
  };
};
/* eslint-enable @typescript-eslint/explicit-function-return-type */
